import React from 'react';
import { render } from 'react-dom';
import cn from 'classnames';
import ContactAvatar from '@components/ContactAvatar';
import Contact from '@models/Contact';
import { localizeNumber } from '@support/number';
import styles from './Annotations.module.scss';
import { ID } from '@app/types';

export type ContactID = ID;
export type ClusterID = number;

const MapAvatar = ({
  contact,
  className,
  includeLabel,
  onHover,
  onClick,
}: {
  contact: Contact;
  className?: string;
  includeLabel?: boolean;
  onHover?: () => void;
  onClick?: () => void;
}) => {
  return (
    <div
      className={cn(styles.mapAvatarContainer, className)}
      onClick={onClick}
      onMouseEnter={onHover}
    >
      <ContactAvatar contact={contact} className={styles.mapAvatar} size="sm" />
      {includeLabel && <div className={styles.mapAvatarLabel}>{contact.displayName}</div>}
    </div>
  );
};

const MapAvatarCluster = ({
  contacts,
  count,
  onHover,
}: {
  contacts: Contact[];
  count: number;
  onHover: () => void;
}) => {
  return (
    <div
      className={cn(styles.mapAvatarCluster, contacts.length < 3 && styles.compactCluster)}
      onMouseEnter={onHover}
    >
      {contacts.map((contact) => (
        <MapAvatar key={contact.id} contact={contact} />
      ))}
      <div className={styles.mapAvatarClusterCount}>{localizeNumber(count)}</div>
    </div>
  );
};

export const createAnnotationFactory =
  (handlers: {
    selectContact: (id: ContactID) => void;
    selectCluster: (id: ClusterID) => void;
    navigateToContact: (id: ContactID) => void;
  }) =>
  (_coordinate, options) => {
    const container = document.createElement('div');

    if (options.data.isCluster) {
      container.classList.add(
        styles.mapCluster,
        `cluster-${options.data.cluster_id}`,
        'annotations-loaded',
      );

      render(
        <MapAvatarCluster
          contacts={options.data.childPoints.map((point) => point.properties)}
          count={options.data.point_count}
          onHover={() => handlers.selectCluster(options.data.cluster_id)}
        />,
        container,
      );
    } else {
      container.classList.add(
        styles.mapAnnotation,
        `contact-${options.data.id}`,
        'annotations-loaded',
      );

      render(
        <MapAvatar
          contact={options.data}
          onHover={() => handlers.selectContact(options.data.id)}
          onClick={() => handlers.navigateToContact(options.data.id)}
          includeLabel
        />,
        container,
      );
    }

    return container;
  };
